import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCloudUploadAlt,
  faInfoCircle,
  faEllipsisV,
  faList
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { productApi } from 'api/endpoints/Product';
import { categoryApi } from 'api/endpoints/Category';
import apiClient from 'api/client';
import { getTenantFromToken } from 'utils/routeUtils';
import { formSubmissionToast } from 'helpers/utils';
import { FileDecoder } from 'api/FileUploadApi';

function CreateProduct() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const tenant = getTenantFromToken();
  const fileInputRef = useRef(null);

  // Form states - General Information
  const [productName, setProductName] = useState('');
  const [inputType, setInputType] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [status, setStatus] = useState('draft');

  // Form states - Pricing & Inventory
  const [price, setPrice] = useState('');
  const [productDiscount, setProductDiscount] = useState('');
  const [isTaxInclusive, setIsTaxInclusive] = useState(true);
  const [taxPercentage, setTaxPercentage] = useState('');
  const [productSku, setProductSku] = useState('');
  const [unitOfMeasurement, setUnitOfMeasurement] = useState('Kgs');
  const [productWeight, setProductWeight] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  // Form states - Publication Settings & Media
  const [productImage, setProductImage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [hasOption, setHasOption] = useState(false);
  const [isMultipleSku, setIsMultipleSku] = useState(false);
  const [isMultiSellerApplicable, setIsMultiSellerApplicable] = useState(false);

  // Options & Option Prices lists
  const [optionsList, setOptionsList] = useState([]);
  const [optionPricesList, setOptionPricesList] = useState([]);

  // UI / API states
  const [categories, setCategories] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Helper to generate SKU
  const generateSKU = name => {
    if (!name) return '';
    const cleanName = name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .substring(0, 6);
    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    return `SKU-${cleanName || 'PROD'}-${randomSuffix}`;
  };

  // Helper to generate option SKU dynamically
  const getOptionSku = (baseSku, optionName) => {
    if (!baseSku) return '';
    if (!optionName) return '';
    const cleanOpt = optionName.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return `${baseSku}-${cleanOpt}`;
  };

  // Fetch categories for dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryApi.getCategoryList();
        if (response.data?.success) {
          setCategories(response.data.results || []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch product details in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchProductDetails = async () => {
        try {
          const response = await productApi.getProduct(id);
          if (response.data?.success) {
            const prod = response.data.results || {};
            setProductName(prod.product_name || '');
            setInputType(prod.input_type || 'Seed');
            setCategoryId(prod.category_id || '');
            setShortDescription(prod.short_description || '');
            setPrice(
              prod.price !== undefined && prod.price !== null
                ? String(prod.price)
                : ''
            );
            setProductDiscount(
              prod.product_discount !== undefined &&
                prod.product_discount !== null
                ? String(prod.product_discount)
                : ''
            );
            setIsTaxInclusive(prod.is_tax_inclusive !== false);
            setTaxPercentage(
              prod.tax_percentage !== undefined && prod.tax_percentage !== null
                ? String(prod.tax_percentage)
                : ''
            );
            setProductSku(prod.product_sku || '');
            setUnitOfMeasurement(prod.unit_of_measurement || 'Kgs');
            setProductWeight(
              prod.product_weight !== undefined && prod.product_weight !== null
                ? String(prod.product_weight)
                : ''
            );

            // Format tags if array
            if (Array.isArray(prod.tags)) {
              setTagsInput(prod.tags.join(', '));
            } else if (prod.tags) {
              setTagsInput(String(prod.tags));
            } else {
              setTagsInput('');
            }

            setProductImage(prod.product_image || '');
            // Fetch presigned URL for displaying image in edit mode
            if (prod.product_image) {
              const presignedUrl = await FileDecoder(prod.product_image);
              setImageUrl(presignedUrl || prod.product_image || '');
            }
            setStatus(prod.status || 'draft');
            setHasOption(prod.has_option === true);
            setOptionsList(Array.isArray(prod.options) ? prod.options : []);
            setOptionPricesList(
              Array.isArray(prod.option_prices) ? prod.option_prices : []
            );
            setIsMultipleSku(prod.is_multiple_sku === true);
            setIsMultiSellerApplicable(
              prod.is_multi_seller_applicable === true
            );
          }
        } catch (error) {
          console.error('Error fetching product details:', error);
          toast.error('Failed to load product details');
        }
      };
      fetchProductDetails();
    }
  }, [id, isEditMode]);

  // Handle value change dirty check
  const handleFieldChange = (setter, value) => {
    setter(value);
    setIsDirty(true);
  };

  // Image Upload handler
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation: max 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit');
      return;
    }

    // Allowed file types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImageUploading(true);
      const response = await apiClient.post(
        '/digitization/v1/utility/upload/image',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const results = response.data?.results || {};
      setImageUrl(results.url || '');
      setProductImage(results.file_key || results.url || '');
      setIsDirty(true);
      toast.success('Product image uploaded successfully');
    } catch (error) {
      console.error('Error uploading product image:', error);
      const errMsg =
        error?.response?.data?.desc ||
        error?.response?.data?.message ||
        'Image upload failed. Please try again.';
      toast.error(errMsg);
    } finally {
      setImageUploading(false);
    }
  };

  // Options Handlers
  const handleAddOption = () => {
    setOptionsList(prev => [
      ...prev,
      {
        option_name: '',
        option_attribute_unit: '',
        sort_order: prev.length + 1,
        option_weight: '',
        is_active: true
      }
    ]);
    setIsDirty(true);
  };

  const handleRemoveOption = index => {
    setOptionsList(prev => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleOptionChange = (index, field, value) => {
    setOptionsList(prev =>
      prev.map((opt, i) => (i === index ? { ...opt, [field]: value } : opt))
    );
    setIsDirty(true);
  };

  // Option Prices Handlers
  const handleAddOptionPrice = () => {
    setOptionPricesList(prev => [
      ...prev,
      {
        option_name: '',
        price: '',
        discount: ''
      }
    ]);
    setIsDirty(true);
  };

  const handleRemoveOptionPrice = index => {
    setOptionPricesList(prev => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleOptionPriceChange = (index, field, value) => {
    setOptionPricesList(prev =>
      prev.map((op, i) => (i === index ? { ...op, [field]: value } : op))
    );
    setIsDirty(true);
  };

  // Dynamic Tax calculations
  const calculateTaxDetails = () => {
    const taxRate = parseFloat(taxPercentage) || 0;
    const baseVal = parseFloat(price) || 0;

    let basePrice = 0;
    let taxAmount = 0;
    let computedFinalPrice = 0;

    if (isTaxInclusive) {
      computedFinalPrice = baseVal;
      taxAmount = baseVal * (taxRate / (100 + taxRate));
      basePrice = computedFinalPrice - taxAmount;
    } else {
      basePrice = baseVal;
      taxAmount = baseVal * (taxRate / 100);
      computedFinalPrice = basePrice + taxAmount;
    }

    return {
      basePrice: basePrice.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      finalPrice: computedFinalPrice.toFixed(2)
    };
  };

  const { basePrice, taxAmount, finalPrice } = calculateTaxDetails();

  // Submit Product
  const handleSubmit = async e => {
    if (e) e.preventDefault();
    if (!productName.trim()) {
      toast.error('Product Name is required');
      return;
    }
    if (!productSku.trim()) {
      toast.error('Product SKU is required');
      return;
    }

    // Process tags
    const tagsArray = tagsInput
      ? tagsInput
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0)
      : [];

    // Map option prices to include dynamically computed SKUs if Multiple SKU toggle is on
    const mappedOptionPrices = optionPricesList.map(op => ({
      ...op,
      price: parseFloat(op.price) || 0,
      discount: parseFloat(op.discount) || 0,
      sku: isMultipleSku ? getOptionSku(productSku.trim(), op.option_name) : ''
    }));

    const payload = {
      product_name: productName.trim(),
      input_type: inputType.trim() || null,
      category_id: categoryId || null,
      short_description: shortDescription.trim() || null,
      price: parseFloat(finalPrice) || 0,
      product_discount: parseFloat(productDiscount) || 0,
      is_tax_inclusive: isTaxInclusive,
      tax_percentage: parseFloat(taxPercentage) || 0,
      product_sku: productSku.trim(),
      unit_of_measurement: unitOfMeasurement,
      product_weight: parseFloat(productWeight) || 0,
      tags: tagsArray,
      product_image: productImage || null,
      status: status,
      has_option: hasOption,
      options: hasOption ? optionsList : [],
      option_prices: hasOption ? mappedOptionPrices : [],
      is_multiple_sku: isMultipleSku,
      is_multi_seller_applicable: isMultiSellerApplicable
    };

    try {
      setSubmitting(true);
      let response;
      if (isEditMode) {
        response = await productApi.updateProduct(id, payload);
      } else {
        response = await productApi.createProduct(payload);
      }
      formSubmissionToast(response, toast, 'Product', isEditMode);
      setIsDirty(false);
      navigate(`/${tenant}/product-report`);
    } catch (error) {
      console.error('Error saving product:', error);
      formSubmissionToast(error, toast, 'Product', isEditMode);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      fluid
      className="px-4 py-3"
      style={{ backgroundColor: '#F8F9FC', minHeight: '100vh' }}
    >
      {/* Top Header Card */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Body className="d-flex justify-content-between align-items-center py-2 px-3">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              onClick={() => navigate(`/${tenant}/product-report`)}
              className="text-secondary p-0 me-3 fs-5"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <h5 className="mb-0 fw-bold" style={{ color: '#0A2540' }}>
              Inventory Management
            </h5>
          </div>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-primary"
              className="d-flex align-items-center px-3 fw-semi-bold btn-sm me-2"
              style={{ borderRadius: '6px', borderWidth: '1.5px' }}
              onClick={() => navigate(`/${tenant}/product-report`)}
            >
              <FontAwesomeIcon icon={faList} className="me-2" />
              View All Products
            </Button>
            <Button variant="link" className="text-secondary p-0 fs-5">
              <FontAwesomeIcon icon={faEllipsisV} />
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Main Title Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2
          className="mb-0 fw-bold"
          style={{ color: '#0A2540', fontSize: '24px' }}
        >
          {isEditMode ? 'Edit Product' : 'Create Product'}
        </h2>
        <div
          style={{ color: '#6C7A9C', fontSize: '14px' }}
          className="fw-semi-bold"
        >
          Inventory &gt;{' '}
          <span className="text-primary">{isEditMode ? 'Edit' : 'New'}</span>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4 mb-5">
          {/* Left Column - Form Inputs */}
          <Col lg={7} md={12}>
            {/* General Info Card */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: '12px' }}
            >
              <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
                <h6 className="fw-bold mb-0" style={{ color: '#0A2540' }}>
                  General Information
                </h6>
              </Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-3">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Product Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Hybrid Tomato Seeds, NPK Fertilizer"
                    value={productName}
                    onChange={e => {
                      setProductName(e.target.value);
                      setIsDirty(true);
                      if (!isEditMode) {
                        setProductSku(generateSKU(e.target.value));
                      }
                    }}
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  />
                </Form.Group>

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Input Type <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Seed, Fertilizer, Tool"
                        value={inputType}
                        onChange={e =>
                          handleFieldChange(setInputType, e.target.value)
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Product Category <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={categoryId}
                        onChange={e =>
                          handleFieldChange(setCategoryId, e.target.value)
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-0">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Short Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter short description of the product..."
                    value={shortDescription}
                    onChange={e =>
                      handleFieldChange(setShortDescription, e.target.value)
                    }
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Pricing & Inventory Card */}
            <Card
              className="shadow-sm border-0"
              style={{ borderRadius: '12px' }}
            >
              <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
                <h6 className="fw-bold mb-0" style={{ color: '#0A2540' }}>
                  Pricing &amp; Logistics
                </h6>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Price (UGX) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={price}
                        onChange={e =>
                          handleFieldChange(setPrice, e.target.value)
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Tax Percentage (%){' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={taxPercentage}
                        onChange={e =>
                          handleFieldChange(setTaxPercentage, e.target.value)
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Product Discount (%){' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={productDiscount}
                        onChange={e =>
                          handleFieldChange(setProductDiscount, e.target.value)
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mb-3 align-items-center">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Tax Inclusion <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="d-flex align-items-center mt-1">
                        <Form.Check
                          type="switch"
                          id="tax-inclusive-switch"
                          checked={isTaxInclusive}
                          onChange={e =>
                            handleFieldChange(
                              setIsTaxInclusive,
                              e.target.checked
                            )
                          }
                          className="fs-4 me-3"
                          style={{ cursor: 'pointer' }}
                        />
                        <span
                          className="fw-semi-bold"
                          style={{ color: '#0A2540', fontSize: '14px' }}
                        >
                          Price is Tax Inclusive
                        </span>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Product SKU (Auto Generated){' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="SKU will auto-generate..."
                        value={productSku}
                        readOnly
                        required
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0',
                          backgroundColor: '#F1F5F9',
                          cursor: 'not-allowed',
                          fontWeight: 'bold'
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Live tax calculations block */}
                <Row className="mb-3">
                  <Col md={12}>
                    <Card
                      style={{
                        backgroundColor: '#F8FAFC',
                        border: '1.5px dashed #E2E8F0',
                        borderRadius: '8px'
                      }}
                    >
                      <Card.Body className="p-3">
                        <Row className="g-3 align-items-center text-center">
                          <Col xs={4}>
                            <div className="text-secondary small fw-bold">
                              Base Price
                            </div>
                            <div className="fs-5 fw-bold text-dark">
                              UGX {basePrice}
                            </div>
                          </Col>
                          <Col
                            xs={4}
                            style={{
                              borderLeft: '1px solid #E2E8F0',
                              borderRight: '1px solid #E2E8F0'
                            }}
                          >
                            <div className="text-secondary small fw-bold">
                              Tax ({taxPercentage}%)
                            </div>
                            <div className="fs-5 fw-bold text-info">
                              UGX {taxAmount}
                            </div>
                          </Col>
                          <Col xs={4}>
                            <div className="text-secondary small fw-bold">
                              Final Price
                            </div>
                            <div className="fs-5 fw-bold text-success">
                              UGX {finalPrice}
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Unit of Measurement{' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={unitOfMeasurement}
                        onChange={e =>
                          handleFieldChange(
                            setUnitOfMeasurement,
                            e.target.value
                          )
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      >
                        <option value="Kgs">Kgs</option>
                        <option value="gram">gram</option>
                        <option value="Pieces">Pieces</option>
                        <option value="Tonne">Tonne</option>
                        <option value="Sachets">Sachets</option>
                        <option value="ml">ml</option>
                        <option value="litre">litre</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label
                        className="fw-bold text-secondary mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        Product Weight <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="0.001"
                        value={productWeight}
                        onChange={e =>
                          handleFieldChange(setProductWeight, e.target.value)
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '10px 14px',
                          border: '1px solid #D8E0F0'
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-0">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Tags (Comma Separated){' '}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. organic, fast-growth, summer"
                    value={tagsInput}
                    onChange={e =>
                      handleFieldChange(setTagsInput, e.target.value)
                    }
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column - Image & Options */}
          <Col lg={5} md={12}>
            {/* Product Image Card */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: '12px' }}
            >
              <Card.Body className="p-4">
                <h6 className="fw-bold mb-3" style={{ color: '#0A2540' }}>
                  Product Image
                </h6>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/jpg"
                  style={{ display: 'none' }}
                />

                <div
                  onClick={triggerFileSelect}
                  className="d-flex flex-column align-items-center justify-content-center text-center p-4 mb-3"
                  style={{
                    border: '2px dashed #CBD5E1',
                    borderRadius: '8px',
                    backgroundColor: '#F8FAFC',
                    minHeight: '180px',
                    cursor: 'pointer'
                  }}
                >
                  {imageUploading ? (
                    <div className="text-center">
                      <div
                        className="spinner-border text-primary mb-2"
                        role="status"
                        style={{ width: '2rem', height: '2rem' }}
                      >
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <p className="text-muted small mb-0">
                        Uploading image, please wait...
                      </p>
                    </div>
                  ) : imageUrl ? (
                    <>
                      <img
                        src={imageUrl}
                        alt="Product Preview"
                        style={{
                          maxHeight: '150px',
                          maxWidth: '100%',
                          borderRadius: '6px',
                          objectFit: 'contain'
                        }}
                      />
                      <p
                        className="text-muted small mt-2 mb-0"
                        style={{ fontSize: '11px' }}
                      >
                        Click to replace image
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="mb-2">
                        <FontAwesomeIcon
                          icon={faCloudUploadAlt}
                          size="2x"
                          className="text-muted mb-2"
                        />
                      </div>
                      <p
                        className="mb-1 fw-semi-bold text-primary"
                        style={{ fontSize: '14px' }}
                      >
                        Click to upload or drag &amp; drop
                      </p>
                      <p className="text-muted small mb-0">
                        PNG, JPG up to 5MB
                      </p>
                    </>
                  )}
                </div>

                <div
                  className="d-flex align-items-start p-3 bg-light"
                  style={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-info mt-1 me-2"
                  />
                  <p
                    className="mb-0 small text-secondary"
                    style={{ lineHeight: '1.4' }}
                  >
                    Image will be used on the marketplaces, POS catalog search,
                    and customer bills.
                  </p>
                </div>
              </Card.Body>
            </Card>

            {/* Specifications / Publication Options Card */}
            <Card
              className="shadow-sm border-0"
              style={{ borderRadius: '12px' }}
            >
              <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
                <h6 className="fw-bold mb-0" style={{ color: '#0A2540' }}>
                  Specifications &amp; Status
                </h6>
              </Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-bold text-secondary mb-2"
                    style={{ fontSize: '13px' }}
                  >
                    Publication Status
                  </Form.Label>
                  <Form.Select
                    value={status}
                    onChange={e => handleFieldChange(setStatus, e.target.value)}
                    style={{
                      borderRadius: '8px',
                      padding: '10px 14px',
                      border: '1px solid #D8E0F0'
                    }}
                  >
                    <option value="draft">Draft (Private)</option>
                    <option value="published">
                      Published (Public Catalog)
                    </option>
                    <option value="archived">Archived</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                    <span
                      className="fw-semi-bold text-secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Has Options
                    </span>
                    <Form.Check
                      type="switch"
                      id="has-option-switch"
                      checked={hasOption}
                      onChange={e => {
                        handleFieldChange(setHasOption, e.target.checked);
                        if (e.target.checked && optionsList.length === 0) {
                          setOptionsList([
                            {
                              option_name: '',
                              option_attribute_unit: '',
                              sort_order: 1,
                              option_weight: '',
                              is_active: true
                            }
                          ]);
                          setOptionPricesList([
                            { option_name: '', price: '', discount: '' }
                          ]);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                    <span
                      className="fw-semi-bold text-secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Multiple SKUs
                    </span>
                    <Form.Check
                      type="switch"
                      id="multiple-sku-switch"
                      checked={isMultipleSku}
                      onChange={e =>
                        handleFieldChange(setIsMultipleSku, e.target.checked)
                      }
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-0">
                  <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                    <span
                      className="fw-semi-bold text-secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Multi-Seller Applicable
                    </span>
                    <Form.Check
                      type="switch"
                      id="multi-seller-switch"
                      checked={isMultiSellerApplicable}
                      onChange={e =>
                        handleFieldChange(
                          setIsMultiSellerApplicable,
                          e.target.checked
                        )
                      }
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Has Option Config Tables */}
        {hasOption && (
          <Row className="g-4 mb-5 pb-5">
            <Col xs={12}>
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: '12px' }}
              >
                <Card.Header className="bg-white border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold mb-0" style={{ color: '#0A2540' }}>
                    Product Options Configuration
                  </h5>
                </Card.Header>
                <Card.Body className="p-4">
                  {/* Options List Table */}
                  <div className="mb-5">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6
                        className="fw-bold mb-0 text-secondary"
                        style={{ fontSize: '15px' }}
                      >
                        Options
                      </h6>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={handleAddOption}
                        className="px-3 fw-bold"
                        style={{
                          backgroundColor: '#6f42c1',
                          borderColor: '#6f42c1',
                          borderRadius: '6px'
                        }}
                      >
                        + Add Option
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '80px' }}>NO</th>
                            <th>OPTION NAME</th>
                            <th>OPTION ATTRIBUTE / UNIT</th>
                            <th style={{ width: '120px' }}>SORT ORDER</th>
                            <th>OPTION WEIGHT</th>
                            <th style={{ width: '100px' }}>STATUS</th>
                            <th style={{ width: '60px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {optionsList.length === 0 ? (
                            <tr>
                              <td
                                colSpan="7"
                                className="text-center text-muted py-3"
                              >
                                No options added yet. Click "+ Add Option" to
                                start.
                              </td>
                            </tr>
                          ) : (
                            optionsList.map((opt, index) => (
                              <tr key={index}>
                                <td className="fw-bold text-secondary">
                                  {index + 1}
                                </td>
                                <td>
                                  <Form.Control
                                    type="text"
                                    placeholder="e.g. Size, Weight, Color"
                                    value={opt.option_name}
                                    onChange={e =>
                                      handleOptionChange(
                                        index,
                                        'option_name',
                                        e.target.value
                                      )
                                    }
                                    style={{ borderRadius: '6px' }}
                                    required
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="text"
                                    placeholder="e.g. Small, Medium, Large"
                                    value={opt.option_attribute_unit}
                                    onChange={e =>
                                      handleOptionChange(
                                        index,
                                        'option_attribute_unit',
                                        e.target.value
                                      )
                                    }
                                    style={{ borderRadius: '6px' }}
                                    required
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    value={opt.sort_order}
                                    onChange={e =>
                                      handleOptionChange(
                                        index,
                                        'sort_order',
                                        parseInt(e.target.value) || 1
                                      )
                                    }
                                    style={{ borderRadius: '6px' }}
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="text"
                                    placeholder="e.g. 500g, 1kg"
                                    value={opt.option_weight}
                                    onChange={e =>
                                      handleOptionChange(
                                        index,
                                        'option_weight',
                                        e.target.value
                                      )
                                    }
                                    style={{ borderRadius: '6px' }}
                                  />
                                </td>
                                <td>
                                  <Form.Check
                                    type="switch"
                                    id={`opt-status-${index}`}
                                    checked={opt.is_active}
                                    onChange={e =>
                                      handleOptionChange(
                                        index,
                                        'is_active',
                                        e.target.checked
                                      )
                                    }
                                    style={{ cursor: 'pointer' }}
                                  />
                                </td>
                                <td className="text-center">
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="text-danger p-0"
                                    onClick={() => handleRemoveOption(index)}
                                  >
                                    ✕
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Option Price Table */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6
                        className="fw-bold mb-0 text-secondary"
                        style={{ fontSize: '15px' }}
                      >
                        Option Price
                      </h6>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={handleAddOptionPrice}
                        disabled={optionsList.length === 0}
                        className="px-3 fw-bold"
                        style={{
                          backgroundColor: '#6f42c1',
                          borderColor: '#6f42c1',
                          borderRadius: '6px'
                        }}
                      >
                        + Add Price
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '80px' }}>S. NO</th>
                            <th>OPTION NAME</th>
                            <th>PRICE (UGX)</th>
                            <th>DISCOUNT (%)</th>
                            {isMultipleSku && <th>OPTION SKU</th>}
                            <th style={{ width: '60px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {optionPricesList.length === 0 ? (
                            <tr>
                              <td
                                colSpan={isMultipleSku ? 6 : 5}
                                className="text-center text-muted py-3"
                              >
                                No option prices added yet. Click "+ Add Price"
                                to start.
                              </td>
                            </tr>
                          ) : (
                            optionPricesList.map((op, index) => {
                              const availableOptionNames = optionsList
                                .map(o => o.option_name.trim())
                                .filter(name => name.length > 0);

                              return (
                                <tr key={index}>
                                  <td className="fw-bold text-secondary">
                                    {index + 1}
                                  </td>
                                  <td>
                                    <Form.Select
                                      value={op.option_name}
                                      onChange={e =>
                                        handleOptionPriceChange(
                                          index,
                                          'option_name',
                                          e.target.value
                                        )
                                      }
                                      style={{ borderRadius: '6px' }}
                                      required
                                    >
                                      <option value="">Select Option</option>
                                      {availableOptionNames.map(
                                        (name, nIdx) => (
                                          <option key={nIdx} value={name}>
                                            {name}
                                          </option>
                                        )
                                      )}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <Form.Control
                                      type="number"
                                      step="0.01"
                                      placeholder="0.00"
                                      value={op.price}
                                      onChange={e =>
                                        handleOptionPriceChange(
                                          index,
                                          'price',
                                          e.target.value
                                        )
                                      }
                                      style={{ borderRadius: '6px' }}
                                      required
                                    />
                                  </td>
                                  <td>
                                    <Form.Control
                                      type="number"
                                      step="0.01"
                                      placeholder="0"
                                      value={op.discount}
                                      onChange={e =>
                                        handleOptionPriceChange(
                                          index,
                                          'discount',
                                          e.target.value
                                        )
                                      }
                                      style={{ borderRadius: '6px' }}
                                    />
                                  </td>
                                  {isMultipleSku && (
                                    <td className="fw-bold text-primary">
                                      {getOptionSku(
                                        productSku,
                                        op.option_name
                                      ) || '-'}
                                    </td>
                                  )}
                                  <td className="text-center">
                                    <Button
                                      type="button"
                                      variant="link"
                                      className="text-danger p-0"
                                      onClick={() =>
                                        handleRemoveOptionPrice(index)
                                      }
                                    >
                                      ✕
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Footer Bar */}
        <div
          className="fixed-bottom d-flex justify-content-between align-items-center px-4 py-3 bg-white border-top shadow-lg"
          style={{ zIndex: 1020 }}
        >
          <div>
            {isDirty && (
              <span className="small text-danger fw-bold">
                Unsaved changes in &quot;
                {isEditMode ? 'Edit Product' : 'New Product'}&quot;
              </span>
            )}
          </div>
          <div>
            <Button
              variant="outline-secondary"
              className="me-3 px-4 fw-semi-bold btn-sm"
              style={{ borderRadius: '6px' }}
              onClick={() => navigate(`/${tenant}/product-report`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              className="px-4 fw-semi-bold btn-sm"
              style={{
                borderRadius: '6px',
                backgroundColor: '#0048A2',
                borderColor: '#0048A2'
              }}
            >
              {submitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Creating...'
                : isEditMode
                ? 'Update Product'
                : 'Create Product'}
            </Button>
          </div>
        </div>
      </Form>
    </Container>
  );
}

export default CreateProduct;
